import { axiosApi } from '@/config';
import { login as loginRequest, logout as logoutRequest } from '../api';
import type { AuthResponseDto, LoginCredentials } from '../interfaces';
import { dispatchAuthSessionCleared } from '@/constants/authEvents';

const ACCESS_TOKEN_KEY = 'cardio-ins::access-token';
const REFRESH_TOKEN_KEY = 'cardio-ins::refresh-token';
const SESSION_EXP_KEY = 'cardio-ins::expires-at';

const storage = {
	set: (key: string, value: string) => {
		sessionStorage.setItem(key, value);
	},
	get: (key: string) => sessionStorage.getItem(key),
	remove: (key: string) => sessionStorage.removeItem(key),
};

const setAuthHeader = (token?: string | null) => {
	if (token) {
		axiosApi.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		delete axiosApi.defaults.headers.common.Authorization;
	}
};

const persistSession = (payload: AuthResponseDto) => {
	storage.set(ACCESS_TOKEN_KEY, payload.accessToken);
	storage.set(REFRESH_TOKEN_KEY, payload.refreshToken);
	storage.set(SESSION_EXP_KEY, String(Date.now() + payload.expiresIn * 1000));
	setAuthHeader(payload.accessToken);
};

const clearSession = () => {
	[ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, SESSION_EXP_KEY].forEach(storage.remove);
	setAuthHeader(undefined);
	dispatchAuthSessionCleared();
};

const retrieveSession = () => {
	const accessToken = storage.get(ACCESS_TOKEN_KEY);
	const refreshToken = storage.get(REFRESH_TOKEN_KEY);
	const expiresAt = storage.get(SESSION_EXP_KEY);

	if (!accessToken || !refreshToken || !expiresAt) {
		return null;
	}

	const expiresIn = Number(expiresAt) - Date.now();

	if (Number.isNaN(expiresIn) || expiresIn <= 0) {
		clearSession();
		return null;
	}

	setAuthHeader(accessToken);

	return {
		accessToken,
		refreshToken,
		expiresAt: Number(expiresAt),
	};
};

export const authService = {
	async login(credentials: LoginCredentials) {
		const response = await loginRequest(credentials);
		persistSession(response);
		return response;
	},

	async logout(accessToken?: string | null) {
		try {
			const token = accessToken ?? storage.get(ACCESS_TOKEN_KEY);
			if (token) {
				await logoutRequest(token);
			}
		} finally {
			clearSession();
		}
	},

	hydrateFromStorage() {
		return retrieveSession();
	},

	clearSession,
};

export type AuthService = typeof authService;
